import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";
import { useTranslation } from "react-i18next";

import { ILogError } from "../../../../models/log-error";

interface ILogErrorDetailsModalProps {
  data: ILogError;
  onClose: () => void;
}

const translationPath = "page.4uBus.logs.externalLogError.details.";

const LogErrorDetailsModal: React.FC<
  ILogErrorDetailsModalProps
> = ({ data, onClose }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return "-";

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "-";

    return format(date, "Pp", {
      locale: language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
    }).replaceAll(",", "");
  };

  const copyText = async (value?: string | null) => {
    if (!value) return;

    await navigator.clipboard.writeText(value);
  };

  const sections = [
    { key: "error_message", value: data.errorMessage },
    { key: "command_text", value: data.commandText },
    { key: "bind_parameters", value: data.bindParameters },
    { key: "stack_trace", value: data.stackTrace },
    { key: "source_class", value: data.sourceClass },
  ];

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogActions>
        <Button onClick={onClose}>X</Button>
      </DialogActions>
      <DialogTitle sx={{ paddingY: 0, paddingX: 6 }}>
        {t(translationPath + "title")} - {data.id}
      </DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 2, py: 2 }}>
        <Stack spacing={0.5}>
          <Typography variant="subtitle2" component="div">
            {t(translationPath + "error_date")}
          </Typography>
          <Typography variant="body2" component="div">
            {formatDate(data.dherro)}
          </Typography>
        </Stack>
        <Stack spacing={0.5}>
          <Typography variant="subtitle2" component="div">
            {t(translationPath + "error_code")}
          </Typography>
          <Typography variant="body2" component="div">
            {data.errorCode ?? "-"}
          </Typography>
        </Stack>
        {sections.map((section) => (
          <Box key={section.key}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
            >
              <Typography variant="subtitle2">
                {t(translationPath + section.key)}
              </Typography>
              {section.value && (
                <Tooltip title={t(translationPath + "copy")}>
                  <IconButton
                    size="small"
                    onClick={() => copyText(section.value)}
                    sx={{ padding: 0.5 }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Typography
              component="pre"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.8rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                backgroundColor: (theme) => theme.palette.grey[100],
                padding: 2,
                borderRadius: 1,
                maxHeight: 220,
                overflow: "auto",
                margin: 0,
              }}
            >
              {section.value || "-"}
            </Typography>
          </Box>
        ))}
      </DialogContent>
    </Dialog>
  );
};

export default LogErrorDetailsModal;
